import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  findReceivableByCode,
  type ReceivableKind,
  type ReceivableMatch,
  type ReceivableRefusal,
} from '@/data/transferCreate';
import PickingScanInput, { type ScanFeedback } from '../../pages/event/order/picking/PickingScanInput';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import { CloseIcon } from '../../ui/CloseIcon';
import { readableErrorMessage } from '../../utils/appErrors';
import { captureOpener, holdFocusOnBackdrop, trapTabKey } from '../receiving/dialogDom';
import './NewTransferDialog.css';

export type ScanToReceiveDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Receiving sites to look at; null means every site the viewer covers. */
  locationIds: string[] | null;
};

const REFUSAL_TEXT: { [reason in ReceivableRefusal]: string } = {
  r2_managed: 'Managed in R2 — receive it in R2',
  not_your_site: 'Not at your sites',
  nothing_outstanding: 'Nothing outstanding',
};

function refusalText(reason: ReceivableRefusal | null): string {
  return reason ? REFUSAL_TEXT[reason] : 'Can’t be received here';
}

function kindLabel(kind: ReceivableKind): string {
  if (kind === 'transfer') return 'Transfer';
  if (kind === 'subrental') return 'Subrental';
  return 'Purchase order';
}

/** The record's receiving section, opened ready to receive. */
export function receiveHref(match: ReceivableMatch): string {
  const id = encodeURIComponent(match.recordId);
  if (match.kind === 'transfer') return `/transfers/${id}/lines?receive=1`;
  if (match.kind === 'subrental') return `/transfers/xrental-orders/${id}/units?receive=1`;
  return `/transfers/purchase-orders/${id}/receiving?receive=1`;
}

type ScanState =
  | { phase: 'idle' }
  | { phase: 'looking'; code: string }
  | { phase: 'choose'; code: string; matches: ReceivableMatch[] }
  | { phase: 'refused'; code: string; matches: ReceivableMatch[] }
  | { phase: 'none'; code: string }
  | { phase: 'error'; code: string; message: string };

type PanelProps = Omit<ScanToReceiveDialogProps, 'open'>;

function ScanToReceivePanel({ onClose, locationIds }: PanelProps) {
  const navigate = useNavigate();
  const titleId = React.useId();
  const leadId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const [opener] = React.useState(captureOpener);
  const [state, setState] = React.useState<ScanState>({ phase: 'idle' });
  const requestRef = React.useRef(0);
  const mountedRef = React.useRef(true);

  useModalScrollLock(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [opener]);

  const openMatch = React.useCallback((match: ReceivableMatch) => {
    onClose();
    navigate(receiveHref(match));
  }, [navigate, onClose]);

  async function lookUp(raw: string): Promise<ScanFeedback | undefined> {
    const code = raw.trim();
    if (!code) return undefined;
    requestRef.current += 1;
    const request = requestRef.current;
    setState({ phase: 'looking', code });
    try {
      const matches = await findReceivableByCode(code, locationIds);
      // A newer scan (or closing the dialog) wins.
      if (request !== requestRef.current || !mountedRef.current) return undefined;
      const receivable = matches.filter((match) => match.receivable);
      if (receivable.length === 1) {
        openMatch(receivable[0]);
        return { success: true, message: `Opening ${receivable[0].docNo ?? kindLabel(receivable[0].kind).toLowerCase()}` };
      }
      if (receivable.length > 1) {
        setState({ phase: 'choose', code, matches: [...receivable, ...matches.filter((match) => !match.receivable)] });
        return { success: true, message: `${receivable.length} records match. Choose one.` };
      }
      if (matches.length > 0) {
        setState({ phase: 'refused', code, matches });
        return { success: false, message: refusalText(matches[0].reason) };
      }
      setState({ phase: 'none', code });
      return { success: false, message: `Nothing matches ${code}` };
    } catch (error) {
      if (request !== requestRef.current || !mountedRef.current) return undefined;
      const message = readableErrorMessage(error, 'The scan could not be looked up. Try again.', 'query');
      setState({ phase: 'error', code, message });
      return { success: false, message };
    }
  }

  // The camera keeps the scan handler it started with, so it calls through a ref to the latest one.
  const lookUpRef = React.useRef(lookUp);
  React.useEffect(() => {
    lookUpRef.current = lookUp;
  });
  const handleScan = React.useCallback((code: string) => lookUpRef.current(code), []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab' && dialogRef.current) {
      trapTabKey(event, dialogRef.current);
    }
    // Keys typed here never reach shortcuts on the page behind the dialog.
    event.stopPropagation();
  }

  function handleOverlayMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    holdFocusOnBackdrop(event);
    event.stopPropagation();
  }

  function stopPropagation(event: React.SyntheticEvent) {
    event.stopPropagation();
  }

  function renderChoices(matches: readonly ReceivableMatch[]) {
    return (
      <ul className="scan-receive__choices" aria-label="Matching records">
        {matches.map((match) => (
          <li key={match.key}>
            <button
              type="button"
              className="scan-receive__choice"
              disabled={!match.receivable}
              onClick={() => openMatch(match)}
            >
              <span className="scan-receive__choice-main">
                <strong>{match.docNo ?? kindLabel(match.kind)}</strong>
                <small>{[kindLabel(match.kind), match.itemName, match.itemSku].filter(Boolean).join(' · ')}</small>
              </span>
              <span className="scan-receive__choice-action">
                {match.receivable ? 'Receive' : refusalText(match.reason)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  let result: React.ReactNode;
  switch (state.phase) {
    case 'idle':
      result = <p className="scan-receive__message">Transfers, purchase orders and subrentals arriving at your sites.</p>;
      break;
    case 'looking':
      result = <p className="scan-receive__message">Looking up {state.code}…</p>;
      break;
    case 'choose':
      result = (
        <>
          <p className="scan-receive__message">{state.code} is on more than one record. Choose the one to receive.</p>
          {renderChoices(state.matches)}
        </>
      );
      break;
    case 'refused':
      result = (
        <>
          <p className="scan-receive__message" data-tone="warn">
            {state.code}: {refusalText(state.matches[0].reason)}.
          </p>
          {state.matches.length > 1 ? renderChoices(state.matches) : null}
        </>
      );
      break;
    case 'none':
      result = (
        <p className="scan-receive__message" data-tone="warn">
          No transfer, purchase order or subrental matches {state.code}.
        </p>
      );
      break;
    case 'error':
      result = <p className="scan-receive__message" data-tone="warn">{state.message}</p>;
      break;
    default:
      result = null;
  }

  return (
    <div
      className="modal-overlay scan-receive-overlay"
      onMouseDown={handleOverlayMouseDown}
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <div
        ref={dialogRef}
        className="modal scan-receive"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={leadId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="modal__header transfer-dialog__header">
          <span className="transfer-dialog__eyebrow">Warehouse</span>
          <h2 id={titleId} className="modal__title transfer-dialog__title">Scan to receive</h2>
          <p id={leadId} className="transfer-dialog__lead">
            Scan a serial, barcode or document number. Its record opens ready to receive.
          </p>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>
        <div className="modal__body">
          <div className="modal__body-content scan-receive__body">
            <PickingScanInput
              onScan={handleScan}
              label="Scan a serial, barcode or Portal label"
              placeholder="Scan, or type a code and press Enter"
              showManualEntry={false}
            />
            <div className="scan-receive__result" aria-live="polite">
              {result}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Scan to receive (Warehouse hero): one scan finds what is arriving. One receivable record opens
 * at its receiving section with `?receive=1`; several are listed to choose from; none, or only
 * refused ones, say why in plain words.
 */
export default function ScanToReceiveDialog({ open, onClose, locationIds }: ScanToReceiveDialogProps) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(<ScanToReceivePanel onClose={onClose} locationIds={locationIds} />, document.body);
}
